import { useState } from 'react'
import { Select } from 'stories/components'
import { isEmpty, removeComma } from 'utils'

const MONTHS_PER_YEAR = 12

export type AmountEntryFrequency = 'Monthly' | 'Annually'

const amountEntryFrequencies: AmountEntryFrequency[] = ['Monthly', 'Annually']

const round2 = (value: number) => Math.round(value * 100) / 100

interface ISelectProps {
  fieldKey: string
  value: AmountEntryFrequency
  onChange: (frequency: AmountEntryFrequency) => void
}

const FrequencySelect = ({ fieldKey, value, onChange }: ISelectProps) => (
  // The dropdown sits inside the field label, which would otherwise pull focus into the input on click.
  <span onClick={(event) => event.stopPropagation()}>
    <Select
      id={`${fieldKey}-entry-frequency`}
      size={3}
      className="-mb-4 w-fit"
      borderClassName="!pr-5 !text-[11px] !text-slate-500 leading-[16px] rounded hover:cursor-pointer"
      options={amountEntryFrequencies}
      value={value}
      onChange={onChange}
    />
  </span>
)

/**
 * Rent, tax and insurance amounts are always stored as monthly figures.
 * This lets a user type them as yearly figures instead, converting on the way in and back out for display.
 */
export const useAmountEntryFrequency = (onChangeAmount: (key: string, value: any) => void) => {
  const [frequencies, setFrequencies] = useState<Record<string, AmountEntryFrequency>>({})
  // What was last typed in yearly mode, so rounding the saved monthly amount cannot alter the input while typing.
  const [yearlyEntries, setYearlyEntries] = useState<Record<string, { typed: string; monthly: number }>>({})

  const isYearly = (key: string) => frequencies[key] === 'Annually'

  const clearYearlyEntry = (key: string) =>
    setYearlyEntries((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })

  const onChangeFrequency = (key: string, frequency: AmountEntryFrequency) => {
    setFrequencies((prev) => ({ ...prev, [key]: frequency }))
    clearYearlyEntry(key)
  }

  const frequencySelect = (key: string) => (
    <FrequencySelect
      fieldKey={key}
      value={frequencies[key] || 'Monthly'}
      onChange={(frequency) => onChangeFrequency(key, frequency)}
    />
  )

  const entryTitle = (key: string, title: string = '') => {
    if (!isYearly(key)) return title
    return /monthly/i.test(title) ? title.replace(/monthly/i, 'Annually') : `Annually ${title}`
  }

  const entryValue = (key: string, monthlyValue: any) => {
    if (!isYearly(key) || isEmpty(monthlyValue)) return monthlyValue
    const lastEntry = yearlyEntries[key]
    if (lastEntry && lastEntry.monthly === removeComma(monthlyValue)) return lastEntry.typed
    return round2(removeComma(monthlyValue) * MONTHS_PER_YEAR)
  }

  const onChangeEntryValue = (key: string, value: any) => {
    if (!isYearly(key)) return onChangeAmount(key, value)
    if (isEmpty(value)) {
      clearYearlyEntry(key)
      return onChangeAmount(key, value)
    }
    const monthly = round2(removeComma(value) / MONTHS_PER_YEAR)
    setYearlyEntries((prev) => ({ ...prev, [key]: { typed: `${value}`, monthly } }))
    onChangeAmount(key, monthly)
  }

  return { frequencySelect, entryTitle, entryValue, onChangeEntryValue }
}
